import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, MessageCircle, Home, Sparkles, Compass, ChevronRight, Users,
} from 'lucide-react';
import { joinWaitlist } from '../lib/waitlist';
import {
  getLastAssessment, rankDimensions, joinLabels, setReturnTo,
} from '../lib/personalization';

const NAVY = '#0F172A';
const INDIGO = '#4F46E5';
const INDIGO_2 = '#6366F1';
const SLATE_2 = '#6B7280';
const SLATE_3 = '#94A3B8';
const BORDER = '#E2E8F0';
const SOFT_BG = '#F8FAFC';

const WA_NUMBER = '918308595553';

function scoreBand(score) {
  if (score >= 80) return { label: 'Thriving',    color: '#059669', bg: '#D1FAE5' };
  if (score >= 65) return { label: 'Strong',      color: '#4F46E5', bg: '#E0E7FF' };
  if (score >= 50) return { label: 'Balanced',    color: '#F59E0B', bg: '#FEF3C7' };
  return              { label: 'Needs Attention', color: '#DC2626', bg: '#FEE2E2' };
}

/* ---------------- Personalized state (score exists) ---------------- */
function PersonalizedExperts({ assessment }) {
  const { strongest, weakest } = useMemo(
    () => rankDimensions(assessment.dimension_scores),
    [assessment],
  );
  const band = scoreBand(assessment.total_score);
  const weakLabels = joinLabels(weakest);

  const message = `Hi Clinity, my Wellness Score is ${assessment.total_score}. I'd like to explore expert guidance, especially for ${weakLabels} wellbeing.`;
  const waUrl = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`;

  const handleConnect = () => {
    joinWaitlist({
      source: 'experts_personalized',
      assessment_id: assessment.assessment_id,
    });
  };

  return (
    <>
      {/* SCORE + AREAS SUMMARY */}
      <div
        className="rounded-[28px] p-8 md:p-10"
        style={{
          background: 'linear-gradient(135deg, #F5F3FF 0%, #EEF2FF 100%)',
          border: '1px solid #E0E7FF',
          boxShadow: '0 24px 60px -30px rgba(79,70,229,0.2)',
        }}
        data-testid="experts-score-summary"
      >
        <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] gap-6 md:gap-10 items-center">
          {/* Score chip */}
          <div className="flex items-center gap-4 md:pr-8 md:border-r" style={{ borderColor: '#E0E7FF' }}>
            <div
              className="w-[88px] h-[88px] rounded-2xl flex flex-col items-center justify-center"
              style={{ background: '#FFFFFF', border: '1px solid #E0E7FF', boxShadow: '0 8px 24px -12px rgba(79,70,229,0.25)' }}
              data-testid="score-chip"
            >
              <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: SLATE_3 }}>Score</div>
              <div className="text-[30px] font-bold leading-none mt-0.5" style={{ color: NAVY, fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.02em' }}>
                {assessment.total_score}
              </div>
              <div className="text-[9px] font-semibold mt-0.5" style={{ color: SLATE_3 }}>/ 100</div>
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: band.color }}>
                {band.label}
              </div>
              <div className="mt-1 text-[15px] font-semibold" style={{ color: NAVY, fontFamily: 'Outfit, sans-serif' }}>
                Your Wellness Score
              </div>
            </div>
          </div>

          {/* Strong / opportunity */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: '#059669' }}>Strongest areas</div>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="dims-strongest">
                {strongest.map((d) => (
                  <span
                    key={d.key}
                    className="px-3 py-1 rounded-full text-[12px] font-semibold bg-white"
                    style={{ color: '#065F46', border: '1px solid #A7F3D0' }}
                  >
                    {d.label} · {d.score}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: '#DC2626' }}>Room to improve</div>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="dims-weakest">
                {weakest.map((d) => (
                  <span
                    key={d.key}
                    className="px-3 py-1 rounded-full text-[12px] font-semibold bg-white"
                    style={{ color: '#991B1B', border: '1px solid #FECACA' }}
                  >
                    {d.label} · {d.score}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PERSONALIZED HEADING */}
      <div className="mt-14 md:mt-20 text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase" style={{ background: '#EEF2FF', color: INDIGO, letterSpacing: '0.22em' }}>
          <Sparkles className="w-3.5 h-3.5" /> For You
        </div>
        <h2
          className="mt-5 text-[30px] sm:text-[40px] lg:text-[48px] font-bold leading-[1.08]"
          style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.028em', color: NAVY }}
          data-testid="experts-personalized-headline"
        >
          Experts who can help you move forward.
        </h2>
        <p
          className="mt-5 text-[15px] sm:text-[17px] leading-[1.65]"
          style={{ color: SLATE_2 }}
          data-testid="experts-personalized-body"
        >
          Based on your score, guidance in <span style={{ color: NAVY, fontWeight: 600 }}>{weakLabels}</span> will move your Wellness Score up the fastest.
        </p>
      </div>

      {/* CTA */}
      <div className="mt-10 md:mt-14 text-center">
        <div
          className="mx-auto max-w-2xl rounded-[28px] px-8 py-12 md:px-12 md:py-14 relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #0F172A 0%, #1E1B4B 60%, #0F0F23 100%)',
            boxShadow: '0 40px 80px -30px rgba(15,23,42,0.55)',
          }}
        >
          <div
            aria-hidden="true"
            className="absolute -top-16 -right-16 w-72 h-72 rounded-full blur-3xl opacity-40 pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.5) 0%, transparent 70%)' }}
          />
          <p className="relative text-[12px] font-semibold uppercase tracking-[0.28em] text-[#C7D2FE]">Personalised for you</p>
          <h3
            className="relative mt-3 text-[22px] sm:text-[28px] font-bold text-white leading-tight max-w-lg mx-auto"
            style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.02em' }}
          >
            Connect with experts across <span style={{ color: '#C4B5FD' }}>{weakLabels}</span> wellbeing.
          </h3>
          <div className="relative mt-8">
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleConnect}
              data-testid="connect-with-experts-btn"
              className="group inline-flex items-center justify-center gap-3 px-9 py-4 sm:px-11 sm:py-[18px] rounded-full font-semibold text-[15px] sm:text-base text-white no-underline transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0"
              style={{
                background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                boxShadow: '0 20px 40px -12px rgba(37,211,102,0.55), inset 0 1px 0 rgba(255,255,255,0.18)',
                letterSpacing: '-0.005em',
              }}
            >
              <MessageCircle className="w-[18px] h-[18px]" strokeWidth={2.2} />
              Connect With Experts
              <ArrowRight className="w-[18px] h-[18px] transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2.2} />
            </a>
            <p className="mt-4 text-xs text-white/60">Your score and priority areas are shared automatically.</p>
          </div>
        </div>
      </div>
    </>
  );
}

/* ---------------- Gated state (no score yet) ---------------- */
function GatedExperts() {
  const navigate = useNavigate();
  const goAssessment = () => {
    setReturnTo('/experts');
    navigate('/assessment');
  };

  return (
    <>
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase" style={{ background: '#EEF2FF', color: INDIGO, letterSpacing: '0.22em' }}>
          <Compass className="w-3.5 h-3.5" /> Start Here
        </div>
        <h1
          className="mt-6 text-[38px] sm:text-[48px] lg:text-[58px] font-bold leading-[1.06]"
          style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.03em', color: NAVY }}
          data-testid="experts-gate-headline"
        >
          Know your Wellness Score first.
        </h1>
        <p
          className="mt-6 text-[16px] sm:text-[18px] leading-[1.65]"
          style={{ color: SLATE_2 }}
          data-testid="experts-gate-body"
        >
          Your Wellness Score helps us understand which areas of your wellbeing could benefit most from the right support — so every expert conversation starts already personalised.
        </p>
        <div className="mt-10">
          <button
            onClick={goAssessment}
            data-testid="experts-gate-cta"
            className="group inline-flex items-center justify-center gap-3 px-9 py-4 sm:px-11 sm:py-[18px] rounded-full font-semibold text-[15px] sm:text-base text-white no-underline transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0"
            style={{
              background: NAVY,
              boxShadow: '0 18px 40px -14px rgba(15,23,42,0.55)',
              letterSpacing: '-0.005em',
            }}
          >
            Get My Wellness Score
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <p className="mt-4 text-xs" style={{ color: SLATE_3 }}>Takes about 2 minutes · 13 questions</p>
        </div>
      </div>

      {/* WHY IT MATTERS */}
      <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { icon: Compass,  title: 'See where you stand',    body: 'A snapshot of your wellbeing across 8 dimensions.' },
          { icon: Sparkles, title: 'Know what to fix first', body: 'The dimensions where guidance matters most today.' },
          { icon: Users,    title: 'Get the right expert',   body: 'We connect you to experts aligned with your priorities.' },
        ].map((c, i) => (
          <div
            key={i}
            className="bg-white rounded-2xl p-6"
            style={{ border: `1px solid ${BORDER}`, boxShadow: '0 2px 6px rgba(15,23,42,0.04)' }}
            data-testid={`experts-why-${i}`}
          >
            <div className="w-10 h-10 rounded-xl mb-4 flex items-center justify-center" style={{ background: '#EEF2FF' }}>
              <c.icon className="w-[18px] h-[18px]" style={{ color: INDIGO }} />
            </div>
            <div className="text-[14px] font-bold" style={{ color: NAVY, fontFamily: 'Outfit, sans-serif' }}>{c.title}</div>
            <div className="mt-1.5 text-[13px] leading-[1.55]" style={{ color: SLATE_2 }}>{c.body}</div>
          </div>
        ))}
      </div>
    </>
  );
}

/* ---------------- Page ---------------- */
export default function ExpertsMarketplace() {
  const navigate = useNavigate();
  const assessment = useMemo(() => getLastAssessment(), []);

  return (
    <div
      className="min-h-screen"
      data-testid="experts-page"
      style={{ background: `linear-gradient(180deg, ${SOFT_BG} 0%, #FFFFFF 100%)`, color: NAVY, fontFamily: 'Manrope, sans-serif' }}
    >
      {/* NAV */}
      <nav className="sticky top-0 z-40 backdrop-blur-xl border-b" style={{ backgroundColor: 'rgba(255,255,255,0.85)', borderColor: BORDER }} data-testid="experts-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <a href="/" className="text-lg font-bold no-underline" style={{ color: NAVY, letterSpacing: '-0.02em', fontFamily: 'Outfit, sans-serif' }}>Clinity</a>
          <button
            onClick={() => navigate('/improve')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all hover:-translate-y-0.5 bg-white"
            style={{ border: `1px solid ${BORDER}`, boxShadow: '0 2px 8px rgba(15,23,42,0.06)' }}
            data-testid="experts-nav-back"
          >
            <ChevronRight className="w-4 h-4 rotate-180" /> Back
          </button>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
        {assessment ? <PersonalizedExperts assessment={assessment} /> : <GatedExperts />}
      </main>

      <div className="pb-14 text-center">
        <button
          onClick={() => navigate('/spaces')}
          className="text-sm font-semibold inline-flex items-center gap-2 transition-transform hover:translate-x-0.5"
          style={{ color: INDIGO_2 }}
          data-testid="experts-spaces-link"
        >
          Also explore Wellness Spaces & Retreats <Home className="w-3.5 h-3.5 -ml-0.5" />
        </button>
      </div>
    </div>
  );
}
